import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {getIdsSelector} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';

import {FlatButton} from '@pb/kit-component-button';
import {Message} from '@pb/kit-component-message';
import {LocaleContext} from '@pb/kit-context-locale';
import {useShown} from '@pb/kit-hooks-use-shown';

import {MAX_SELECTED_ENTITLEMENTS} from '../constants';
import {RolesGrantsResourcesExportResultModal} from '../roles-grants-resources.export-result.modal';
import {RolesGrantsResourcesImportResultModal} from '../roles-grants-resources.import-result.modal';
import {RolesGrantsResourcesImportModal} from '../roles-grants-resources.import.modal';
import {
    buildRelationsFromSelection,
    buildRelationsIndex,
    downloadEntitlementTransferContext,
    downloadImportResultsReport,
    mergeRelationsIndex,
} from '../utils';

import {useExportEntitlements} from './useExportEntitlements';
import {useGetCurrentEnvironment} from './useGetCurrentEnvironment';
import {useImportEntitlements} from './useImportEntitlements';

import type {ResourceView} from 'src/modules/entitlements/typedef';
import type {EntitlementTransferContext} from 'src/openapi/admin/models/EntitlementTransferContext';
import type {MigrationRelationResult} from 'src/openapi/admin/models/MigrationRelationResult';
import type {Relation} from 'src/openapi/admin/models/Relation';

type Params = {
    rows: ResourceView[],
    selectionKey: string,
};

export const useEntitlementsTransfer = ({rows, selectionKey}: Params) => {
    const {hasAccess} = useAdminAccessRight();
    const {environmentValue} = useGetCurrentEnvironment();

    const isProductionEnvironment = environmentValue === 'production';

    const showExportBtn = useMemo(() => hasAccess(ACCESS_RIGHTS.EXPORT_ENTITLEMENTS), [hasAccess]);

    const showImportBtn = useMemo(() => hasAccess(ACCESS_RIGHTS.IMPORT_ENTITLEMENTS), [hasAccess])
        && !isProductionEnvironment;

    const {locale} = useContext(LocaleContext);
    const localeDateFormat = locale?.dateFormat;
    const selectedIdsSelector = useMemo(() => getIdsSelector(selectionKey), [selectionKey]);
    const selectedIds = useSelector(selectedIdsSelector) as Array<string | number>;
    const relationsIndexFromRows = useMemo(() => buildRelationsIndex(rows), [rows]);
    const [relationsIndex, setRelationsIndex] = useState(relationsIndexFromRows);
    const isSelectionEmpty = selectedIds.length === 0;

    useEffect(() => {
        if (isSelectionEmpty) {
            setRelationsIndex(relationsIndexFromRows);
            return;
        }

        setRelationsIndex(prev => mergeRelationsIndex(prev, relationsIndexFromRows));
    }, [isSelectionEmpty, relationsIndexFromRows]);

    const selectedRelations = useMemo(
        () => buildRelationsFromSelection(selectedIds, relationsIndex),
        [selectedIds, relationsIndex],
    );

    const {
        exportRelations,
        exportResult,
        isExporting,
        exportError,
        clearExportResult,
        clearExportError,
    } = useExportEntitlements();

    const {
        importContext,
        importError,
        isImporting,
        resetImportState,
    } = useImportEntitlements();

    const [importResultData, setImportResultData] = useState<MigrationRelationResult[] | null>(null);

    const {
        shown: exportModalShown,
        onClick: openExportModal,
        onClose: closeExportModal,
    } = useShown();

    const {
        shown: importModalShown,
        onClick: openImportModal,
        onClose: closeImportModal,
    } = useShown();

    const {
        shown: importResultModalShown,
        onClick: openImportResultModal,
        onClose: closeImportResultModal,
    } = useShown();

    const handleExportClick = useCallback(async (relations: Relation[]) => {
        const result = await exportRelations(relations);
        if (result?.success) {
            openExportModal();
        }
    }, [exportRelations, openExportModal]);

    const handleExportClose = useCallback(() => {
        closeExportModal();
        clearExportResult();
    }, [closeExportModal, clearExportResult]);

    const handleExportConfirm = useCallback((context: EntitlementTransferContext) => {
        downloadEntitlementTransferContext(context, 'entitlements-export.json', localeDateFormat);
        closeExportModal();
        clearExportResult();
    }, [closeExportModal, clearExportResult, localeDateFormat]);

    const handleOpenImportModal = useCallback(() => {
        resetImportState();
        openImportModal();
    }, [openImportModal, resetImportState]);

    const handleCloseImportModal = useCallback(() => {
        closeImportModal();
        resetImportState();
    }, [closeImportModal, resetImportState]);

    const handleImportConfirm = useCallback(async (context: EntitlementTransferContext) => {
        const result = await importContext(context);
        if (result?.success) {
            setImportResultData(result.data ?? []);
            handleCloseImportModal();
            openImportResultModal();
        }
    }, [importContext, handleCloseImportModal, openImportResultModal]);

    const handleImportResultClose = useCallback(() => {
        setImportResultData(null);
        closeImportResultModal();
        resetImportState();
    }, [closeImportResultModal, resetImportState]);

    const handleDownloadImportResults = useCallback(() => {
        if (importResultData && importResultData.length) {
            downloadImportResultsReport(importResultData, 'entitlements-import-report.csv', localeDateFormat);
        }
    }, [importResultData, localeDateFormat]);

    const exportErrorMessage = exportError ? (
        <Message color="error" onClose={clearExportError}>
            {exportError}
        </Message>
    ) : null;

    const importButton = showImportBtn ? (
        <FlatButton onClick={handleOpenImportModal} disabled={isImporting}>
            <b>Import Entitlements</b>
        </FlatButton>
    ) : null;

    const renderExportButton = (ids: Array<number | string>) => {
        if (!showExportBtn) {
            return null;
        }

        const isExportDisabled = ids.length > MAX_SELECTED_ENTITLEMENTS;
        const exportLimitTooltip = isExportDisabled
            ? `Max. entitlements count: ${MAX_SELECTED_ENTITLEMENTS} items`
            : undefined;

        return (
            <FlatButton
                disabled={isExportDisabled}
                title={exportLimitTooltip}
                onClick={() => handleExportClick(selectedRelations)}>
                <b>Export Entitlements</b>
            </FlatButton>
        );
    };

    const transferModals = (
        <>
            <RolesGrantsResourcesImportModal
                shown={importModalShown}
                onClose={handleCloseImportModal}
                onImport={handleImportConfirm}
                busy={isImporting}
                apiError={importError}
            />
            <RolesGrantsResourcesImportResultModal
                shown={importResultModalShown}
                results={importResultData}
                onClose={handleImportResultClose}
                onDownload={importResultData?.length ? handleDownloadImportResults : undefined}
            />
            <RolesGrantsResourcesExportResultModal
                shown={exportModalShown}
                data={exportResult}
                onClose={handleExportClose}
                onExport={handleExportConfirm}
                busy={isExporting}
            />
        </>
    );

    return {
        exportErrorMessage,
        importButton,
        renderExportButton,
        transferModals,
    };
};
